/* eslint-disable @typescript-eslint/no-explicit-any */
import { useLocation } from '@reach/router'
import tw from 'twin.macro'
import { graphql, navigate } from 'gatsby'
import { localizedPath, getLocale } from '@plugins/bw-i18next/utils'
import SetOfPricing from '@/components/section/set-of-pricing'
import { slugify } from '@/utils/strings'
import '@/components/ui/pricing-card'
import React, { useState } from 'react'
import { extractDataAttributes } from '@/utils/dom'

export const fragment = graphql`
  fragment SectionTabSection on ContentfulSectionTabSection {
    __typename
    activeTab
    contentful_id
    internalName
    headline
    tabs {
      ... on ContentfulSectionSetOfPricing {
        ...SectionSetOfPricing
        nt_experiences {
          ...ExperienceContent
          ... on ContentfulNinetailedExperience {
            variants: nt_variants {
              ...SectionSetOfPricing
            }
          }
        }
      }
    }
  }
`

export default function TabSection({
  activeTab = 0,
  contentfulId,
  headline,
  internalName,
  tabs,
  isHero = false,
  ...rest
}): JSX.Element {
  const dataAttributes = extractDataAttributes(rest)
  const [activeTabIdx, setActiveTabIdx] = useState(activeTab ? activeTab - 1 : 0)

  const { pathname } = useLocation()

  const handleSetActiveTab = (idx) => {
    if (idx === activeTabIdx) {
      return
    }

    const personalPricingPath = localizedPath('/pricing/', getLocale(pathname))
    const businessPricingPath = localizedPath('/pricing/business/', getLocale(pathname))

    if (pathname === personalPricingPath) {
      navigate(businessPricingPath, { state: { disableScrollUpdate: true } })
    } else if (pathname === businessPricingPath) {
      navigate(personalPricingPath, { state: { disableScrollUpdate: true } })
    }
    setActiveTabIdx(idx)
  }

  const Headline = isHero ? 'h1' : 'h3'

  return (
    <section tw="max-w-8xl mx-auto lg:px-9 text-center" id={slugify(internalName)} {...dataAttributes}>
      <div tw="bg-primaryBlue lg:rounded-4xl py-20">
        {headline && <Headline tw="h3 mx-3 mb-8 text-white">{headline}</Headline>}
        <div tw="mx-auto">
          <div tw="bg-deepBlue w-auto p-1 inline-block rounded-full transition-all" role="tablist">
            {tabs.map((tab, idx) => (
              <button
                key={`tab-${contentfulId}-${idx}`}
                data-testid={`tab-${contentfulId}-${idx}`}
                id={`tab-${contentfulId}-${idx}`}
                onClick={() => handleSetActiveTab(idx)}
                tabIndex={idx === 0 ? 0 : -1}
                type="button"
                role="tab"
                aria-selected={idx === activeTabIdx}
                aria-controls={`tabpanel-${contentfulId}-${idx}`}
                tw="py-4 px-8 lg:py-4 lg:px-12 transition-all"
                css={[idx === activeTabIdx ? tw`!bg-white rounded-full text-primaryBlue` : tw`text-white`]}
              >
                {tab.headline || 'Missing Title!'}
              </button>
            ))}
          </div>
          {tabs.map((tab, idx) => (
            <div
              role="tabpanel"
              aria-labelledby={`tab-${contentfulId}-${idx}`}
              key={`tabpanel-${contentfulId}-${idx}`}
              id={`tabpanel-${contentfulId}-${idx}`}
              data-testid={`tabpanel-${contentfulId}-${idx}`}
              css={[idx === activeTabIdx ? tw`block` : tw`hidden`]}
              tw="px-5 lg:px-0"
            >
              <div tw="-mt-6">
                <SetOfPricing {...tab} hideHeadline insideTabs />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
